import React, { useEffect, useRef, useState } from 'react';
import {resolveAppearance} from '../utils/uiUtils';
import { Form, Button } from 'react-bootstrap';
import { Field, Screen } from '../types';
import { isValidNumericInput } from '../utils/numberInput';
import { DateTimePicker } from './DateTimePicker';
import { DurationPicker } from './DurationPicker';
import { fileToBase64, downloadFile, formatFileSize, FileData, isDisplayableImage, getFileName, IMAGE_UPLOAD_TYPES, isSupportedImageUpload } from '../utils/fileUtils';
import { IconCamera, IconDownload, IconTrash, IconScan, IconEye, IconEyeOff, IconExternalLink, IconX, IconSelect, IconUpload } from '@tabler/icons-react';
import { AuthenticatedImage } from './AuthenticatedImage';
import { ImageModal } from './ImageModal';
import { PhotoCapture } from './PhotoCapture';
import { BarcodeScanner } from './BarcodeScanner';
import MDEditor from '@uiw/react-md-editor';
import { M2ORecordTable } from './M2ORecordTable';
import { M2ORecordBadges } from './M2ORecordBadges';
import { BreadcrumbEntry } from '../utils/breadcrumbStack';
import { gpsCoordinateOf } from '../utils/deviceLocation';

interface FormFieldRendererProps {
    field: Field;
    value: any;
    error?: string;
    onChange: (value: any) => void;
    onErrorClear?: () => void;
    disabled?: boolean;
    displayValue?: string;
    onOpenSelectModal?: (field: Field) => void;
    allScreens?: Screen[];
    rawValue?: any;
    onClear?: () => void;
    breadcrumbEntry?: BreadcrumbEntry;
    /** Put the cursor here when the form opens (the form's first usable field). */
    autoFocus?: boolean;
    /** Start the camera on a focused barcode field. Creating only - editing an
     *  existing record must never be interrupted by the scanner. */
    autoScan?: boolean;
    /** All values of the open form and its screen - a picked record is previewed with the picker's parameters. */
    formValues?: Record<string, any>;
    formScreen?: any;
    /** rawValue arrived with the form's record (true), or is a row just picked from the field's screen (false). */
    rawValueFromRecord?: boolean;
    /** Dynamic form: the saved related record as the form's record carries it, in the shape of the field's screen. */
    relatedRecord?: any;
}

export const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({
    field,
    value,
    error,
    onChange,
    onErrorClear,
    disabled = false,
    displayValue,
    onOpenSelectModal,
    allScreens,
    rawValue,
    onClear,
    breadcrumbEntry,
    autoFocus = false,
    autoScan = false,
    formValues,
    formScreen,
    rawValueFromRecord = false,
    relatedRecord
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
    const [showPhotoCapture, setShowPhotoCapture] = useState(false);
    const [fileError, setFileError] = useState<string | null>(null);
    const isImageField = field.type === 'image';
    const [passwordInput, setPasswordInput] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordMismatch, setPasswordMismatch] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Field is disabled if either disabled prop is true OR field.editable is false
    const isDisabled = disabled || field.editable === false;
    const fieldUi = resolveAppearance(field.appearance, 'formField');
    const groupClassName = ['mb-3', fieldUi.className, fieldUi.groupClassName].filter(Boolean).join(' ');
    const groupStyle = fieldUi.style;

    // A new record's first field being a barcode means it is there to be
    // scanned, so start the camera straight away. Editing never does this: the
    // cursor goes into the input and the code is scanned only on demand.
    const isEmpty = value === null || value === undefined || value === '';
    useEffect(() => {
        if (autoFocus && autoScan && field.type === 'barcode' && !isDisabled && isEmpty) {
            setShowBarcodeScanner(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoFocus, autoScan, field.type, isDisabled]);

    const handleChange = (newValue: any) => {
        onChange(newValue);
        if (error && onErrorClear) {
            onErrorClear();
        }
    };

    const acceptFile = async (file: File) => {
        if (isImageField && !isSupportedImageUpload(file)) {
            setFileError('Vyberte obrázok JPEG, PNG, GIF, WebP alebo AVIF.');
            return;
        }
        setFileError(null);
        const fileData = await fileToBase64(file);
        if (fileData) {
            handleChange(fileData);
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Allow selecting the same file again after a rejected upload.
        e.target.value = '';
        if (file) await acceptFile(file);
    };

    const handleFileRemove = () => {
        setFileError(null);
        handleChange(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handlePaste = async (e: React.ClipboardEvent) => {
        const files = e.clipboardData?.files;
        if (files && files.length > 0) {
            e.preventDefault();
            await acceptFile(files[0]);
        }
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const files = e.dataTransfer?.files;
        if (files && files.length > 0) {
            await acceptFile(files[0]);
        }
    };

    switch (field.type) {
        case 'string':
            return (
                <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                    <Form.Label>
                        {field.displayName}
                        {field.required && <span className="text-danger">*</span>}
                    </Form.Label>
                    <Form.Control
                        type="text"
                        value={value}
                        onChange={(e) => handleChange(e.target.value)}
                        required={field.required}
                        disabled={isDisabled}
                        isInvalid={!!error}
                        autoFocus={autoFocus}
                    />
                    {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
                </Form.Group>
            );

        case 'text':
            return (
                <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                    <Form.Label>
                        {field.displayName}
                        {field.required && <span className="text-danger">*</span>}
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={value || ''}
                        onChange={(e) => handleChange(e.target.value)}
                        required={field.required}
                        disabled={isDisabled}
                        isInvalid={!!error}
                        autoFocus={autoFocus}
                    />
                    {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
                </Form.Group>
            );

        case 'float':
        case 'number':
            // A GPS coordinate comes from the device, not from the user: the
            // form reads it on its own and the field is not shown.
            if (gpsCoordinateOf(field)) {
                return null;
            }
            return (
                <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                    <Form.Label>
                        {field.displayName}
                        {field.required && <span className="text-danger">*</span>}
                    </Form.Label>
                    <Form.Control
                        type="text"
                        inputMode="decimal"
                        value={value}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (isValidNumericInput(val)) {
                                handleChange(val);
                            }
                        }}
                        required={field.required}
                        disabled={isDisabled}
                        isInvalid={!!error}
                        autoFocus={autoFocus}
                    />
                    {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
                </Form.Group>
            );

        case 'integer':
            return (
                <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                    <Form.Label>
                        {field.displayName}
                        {field.required && <span className="text-danger">*</span>}
                    </Form.Label>
                    <Form.Control
                        // Text with a numeric keypad rather than type="number": the
                        // spinner reacts to a stray scroll and the phone keyboard is
                        // not always the numeric one.
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={value ?? ''}
                        onChange={(e) => {
                            const digits = e.target.value.replace(/[^\d-]/g, '');
                            handleChange(digits === '' || digits === '-' ? '' : parseInt(digits, 10));
                        }}
                        required={field.required}
                        disabled={isDisabled}
                        isInvalid={!!error}
                        autoFocus={autoFocus}
                        className={error ? 'form-control is-invalid' : 'form-control'}
                    />
                    {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
                </Form.Group>
            );

        case 'boolean':
            return (
                <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                    <label className="form-check form-switch">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={value === true || value === 'true'}
                            onChange={(e) => handleChange(e.target.checked)}
                            disabled={isDisabled}
                        />
                        <span className="form-check-label">
                            {field.displayName}
                        </span>
                    </label>
                </Form.Group>
            );

        case 'created-by':
        case 'updated-by': {
                const targetColumnName = field.targetColumn?.columnName;
                const createdByDisplay = displayValue || (typeof value === 'object'
                    ? (targetColumnName && value?.[targetColumnName]) || value?.name || value?.full_name || value?.email || value?.id
                    : value);
                return (
                    <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                        <Form.Label>{field.displayName}</Form.Label>
                        <Form.Control
                            type="text"
                            value={createdByDisplay || ''}
                            readOnly
                            disabled
                        />
                    </Form.Group>
                );
            }
        case 'user':
        case 'many-to-one':
            if (field.screenId && onOpenSelectModal) {
                const targetScreen = allScreens?.find(s => s.id === field.screenId);
                const useTable = !!field.displayAsTable && !!targetScreen;

                // The field's screen offers what can be chosen; its filter may stop listing a record once it
                // is saved, so a saved value is never requested through it. It comes with the form's record:
                //  - classic form: as the value itself, shaped by the related screen of the form screen's column;
                //  - dynamic form: as relatedRecord, shaped by the field's screen.
                // A row just picked from the field's screen is held as it was listed.
                const rawRecord = rawValue && typeof rawValue === 'object' ? rawValue : undefined;
                let heldRecord: any;
                let heldScreen: any;
                if (!rawValueFromRecord) {
                    heldRecord = rawRecord;
                    heldScreen = targetScreen;
                } else if (field.screenColumnId) {
                    heldRecord = rawRecord;
                    heldScreen = formScreen?.columns?.find((column: any) => column.id === field.screenColumnId)?.relatedScreen;
                } else {
                    heldRecord = relatedRecord;
                    heldScreen = targetScreen;
                }
                if (!heldScreen) heldRecord = undefined;
                // Saved, but nothing came to preview it with: the label that came with the record is shown as text.
                const labelOnly = rawValueFromRecord && !!rawRecord && !heldRecord;

                if (useTable && targetScreen && !labelOnly) {
                    const recordId = rawValue && typeof rawValue === 'object' ? rawValue.id : (typeof value === 'number' || typeof value === 'string' ? value : null);
                    const hasValue = recordId !== null && recordId !== undefined && recordId !== '';
                    const canClear = !isDisabled && !field.required && hasValue && onClear;

                    return (
                        <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                            <Form.Label>
                                {field.displayName}
                                {field.required && <span className="text-danger">*</span>}
                            </Form.Label>
                            <div className={`card${error ? ' border-danger' : ''}`}>
                                {hasValue && (
                                    <M2ORecordTable screen={heldRecord ? heldScreen : targetScreen} recordId={recordId!} record={heldRecord}
                                                    breadcrumbEntry={breadcrumbEntry}
                                                    formValues={formValues} formScreen={formScreen} />
                                )}
                                {!isDisabled && (
                                    <div className="card-footer d-flex justify-content-end gap-2 py-2">
                                        {canClear && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={onClear}
                                                type="button"
                                            >
                                                <IconX size={16} /> Vymazať
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => onOpenSelectModal(field)}
                                            type="button"
                                        >
                                            <IconSelect size={16} /> {hasValue ? 'Zmeniť' : 'Vybrať'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                            {error && <div className="invalid-feedback d-block">{error}</div>}
                        </Form.Group>
                    );
                }

                const noTargetColumn = !field.targetColumn;
                const badgeRecordId = rawValue && typeof rawValue === 'object' ? rawValue.id : (typeof value === 'number' || typeof value === 'string' ? value : null);
                const hasBadgeValue = badgeRecordId !== null && badgeRecordId !== undefined && badgeRecordId !== '';

                if (noTargetColumn && targetScreen && hasBadgeValue && !labelOnly) {
                    return (
                        <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                            <Form.Label>
                                {field.displayName}
                                {field.required && <span className="text-danger">*</span>}
                            </Form.Label>
                            <M2ORecordBadges
                                screen={heldRecord ? heldScreen : targetScreen}
                                recordId={badgeRecordId!}
                                record={heldRecord}
                                onClick={() => onOpenSelectModal(field)}
                                isInvalid={!!error}
                                disabled={isDisabled}
                                formValues={formValues}
                                formScreen={formScreen}
                            />
                            {error && <div className="invalid-feedback d-block">{error}</div>}
                        </Form.Group>
                    );
                }

                const display = displayValue || value;
                const inputHasValue = display !== null && display !== undefined && display !== '';
                const showClear = !isDisabled && !field.required && inputHasValue && !!onClear;
                return (
                    <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                        <Form.Label>
                            {field.displayName}
                            {field.required && <span className="text-danger">*</span>}
                        </Form.Label>
                        <div className="position-relative">
                            <Form.Control
                                type="text"
                                value={display}
                                readOnly
                                required={field.required}
                                placeholder={isDisabled ? '' : 'Kliknite pre výber...'}
                                disabled={isDisabled}
                                isInvalid={!!error}
                                onClick={() => !isDisabled && onOpenSelectModal(field)}
                                style={{ cursor: isDisabled ? 'default' : 'pointer', paddingRight: showClear ? '2.25rem' : undefined }}
                            />
                            {showClear && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onClear?.(); }}
                                    aria-label="Vymazať"
                                    className="btn btn-link p-0 position-absolute top-50 translate-middle-y text-secondary"
                                    style={{ right: '0.5rem', lineHeight: 1 }}
                                >
                                    <IconX size={16} />
                                </button>
                            )}
                            {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
                        </div>
                    </Form.Group>
                );
            }
            return (
                <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                    <Form.Label>
                        {field.displayName}
                        {field.required && <span className="text-danger">*</span>}
                    </Form.Label>
                    <Form.Control
                        type="text"
                        value={displayValue || value}
                        onChange={(e) => handleChange(e.target.value)}
                        required={field.required}
                        disabled={isDisabled}
                    />
                </Form.Group>
            );

        case 'timestamp':
            return (
                <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                    <Form.Label>
                        {field.displayName}
                        {field.required && <span className="text-danger">*</span>}
                    </Form.Label>
                    <DateTimePicker
                        mode="datetime"
                        value={value}
                        onChange={handleChange}
                        className={`form-control${error ? ' is-invalid' : ''}`}
                        disabled={isDisabled}
                        isClearable={!field.required}
                    />
                    {error && <div className="invalid-feedback d-block">{error}</div>}
                </Form.Group>
            );

        case 'time':
            return (
                <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                    <Form.Label>
                        {field.displayName}
                        {field.required && <span className="text-danger">*</span>}
                    </Form.Label>
                    <DateTimePicker
                        mode="time"
                        value={value}
                        onChange={handleChange}
                        className={`form-control${error ? ' is-invalid' : ''}`}
                        disabled={isDisabled}
                        isClearable={!field.required}
                    />
                    {error && <div className="invalid-feedback d-block">{error}</div>}
                </Form.Group>
            );

        case 'interval':
            return (
                <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                    <Form.Label>
                        {field.displayName}
                        {field.required && <span className="text-danger">*</span>}
                    </Form.Label>
                    <DurationPicker
                        value={value ?? ''}
                        onChange={handleChange}
                        required={field.required}
                        disabled={isDisabled}
                        isInvalid={!!error}
                        isClearable={!field.required}
                        autoFocus={autoFocus}
                    />
                    <Form.Text className="text-muted">Trvanie vo formáte HH:MM (napr. 01:30, 25:00)</Form.Text>
                    {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
                </Form.Group>
            );

        case 'month-year':
            return (
                <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                    <Form.Label>
                        {field.displayName}
                        {field.required && <span className="text-danger">*</span>}
                    </Form.Label>
                    <DateTimePicker
                        mode="month"
                        value={value}
                        onChange={handleChange}
                        className={`form-control${error ? ' is-invalid' : ''}`}
                        disabled={isDisabled}
                        isClearable={!field.required}
                    />
                    {error && <div className="invalid-feedback d-block">{error}</div>}
                </Form.Group>
            );

        case 'date':
            return (
                <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                    <Form.Label>
                        {field.displayName}
                        {field.required && <span className="text-danger">*</span>}
                    </Form.Label>
                    <DateTimePicker
                        mode="date"
                        value={value}
                        onChange={handleChange}
                        className={`form-control${error ? ' is-invalid' : ''}`}
                        disabled={isDisabled}
                        isClearable={!field.required}
                    />
                    {error && <div className="invalid-feedback d-block">{error}</div>}
                </Form.Group>
            );

        case 'file':
        case 'image':
            const fileData = value as FileData | null;
            const isImage = fileData && isDisplayableImage(fileData.mimeType);
            const filename = fileData ? getFileName(fileData) : '';

            return (
                <>
                    <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                        <Form.Label>
                            {field.displayName}
                            {field.required && <span className="text-danger">*</span>}
                        </Form.Label>

                        {/* Above the preview on purpose: on a phone a 300px thumbnail
                            pushes anything below it off the screen. */}
                        {!isDisabled && (
                            <div className="d-flex flex-wrap gap-2 mb-2">
                                {isImageField && <Button
                                    variant="primary"
                                    size="sm"
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPhotoCapture(true); }}
                                >
                                    <IconCamera size={16} className="me-1" />
                                    {fileData ? 'Odfotiť znova' : 'Odfotiť'}
                                </Button>}
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); fileInputRef.current?.click(); }}
                                >
                                    <IconUpload size={16} className="me-1" />
                                    {isImageField ? (fileData ? 'Nahradiť obrázok' : 'Vybrať obrázok') : (fileData ? 'Nahradiť súbor' : 'Vybrať súbor')}
                                </Button>
                            </div>
                        )}

                        {/* Image preview - clickable to enlarge */}
                        {fileData && isImage && fileData.url && (
                            <div
                                className="mb-2"
                                onClick={() => setShowImageModal(true)}
                                style={{ cursor: 'pointer' }}
                            >
                                <AuthenticatedImage
                                    url={fileData.url}
                                    alt={filename}
                                    className="img-thumbnail file-preview-thumb"
                                />
                            </div>
                        )}

                        {fileData && isImage && fileData.data && (
                            <div
                                className="mb-2"
                                onClick={() => setShowImageModal(true)}
                                style={{ cursor: 'pointer' }}
                            >
                                <img
                                    src={`data:${fileData.mimeType};base64,${fileData.data}`}
                                    alt={filename}
                                    className="img-thumbnail file-preview-thumb"
                                />
                            </div>
                        )}

                        {/* File info and actions */}
                        {fileData && (
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="flex-grow-1">
                                    <div className="text-muted small">
                                        {filename}
                                        {fileData.size && ` (${formatFileSize(fileData.size)})`}
                                    </div>
                                </div>
                                {/* Download button only for non-images; a read-only
                                    field still lets the person take the file. */}
                                {!isImage && (
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => downloadFile(fileData)}
                                    >
                                        <IconDownload size={16} />
                                    </Button>
                                )}
                                {!isDisabled && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={handleFileRemove}
                                    >
                                        <IconTrash size={16} />
                                    </Button>
                                )}
                            </div>
                        )}

                        {/* Drop zone with paste, drag-drop, and click-to-browse */}
                        {!isDisabled && (
                            <>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={isImageField ? IMAGE_UPLOAD_TYPES.join(',') : undefined}
                                    aria-label={isImageField ? 'Vybrať obrázok' : 'Vybrať súbor'}
                                    className="d-none"
                                    onChange={handleFileChange}
                                    required={field.required && !fileData}
                                />
                                <div
                                    tabIndex={0}
                                    onPaste={handlePaste}
                                    onDrop={handleDrop}
                                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                    onDragLeave={() => setIsDragging(false)}
                                    className="text-center text-muted d-none d-sm-block"
                                    style={{
                                        border: `2px dashed ${isDragging ? '#467fd0' : '#d1d5db'}`,
                                        borderRadius: '8px',
                                        padding: '20px',
                                        cursor: 'default',
                                        backgroundColor: isDragging ? 'rgba(70,127,208,0.05)' : 'transparent',
                                        outline: 'none',
                                        transition: 'border-color 0.15s, background-color 0.15s',
                                    }}
                                >
                                    <div style={{ fontSize: '0.9em' }}>
                                        <a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); fileInputRef.current?.click(); }}>{isImageField ? (fileData ? 'Nahraďte obrázok' : 'Vyberte obrázok') : (fileData ? 'Nahraďte súbor' : 'Vyberte súbor')}</a>, vložte <kbd>{/Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘' : 'Ctrl'}+V</kbd> alebo presuňte sem
                                    </div>
                                </div>
                                {fileData && (
                                    <Form.Text className="text-muted">
                                        {isImageField ? 'Nahrajte nový obrázok pre nahradenie existujúceho' : 'Nahrajte nový súbor pre nahradenie existujúceho'}
                                    </Form.Text>
                                )}
                            </>
                        )}

                        {(fileError || error) && <div className="invalid-feedback d-block" role="alert">{fileError || error}</div>}
                    </Form.Group>

                    {/* Camera capture — the photo lands in the same shape as an upload */}
                    {!isDisabled && isImageField && (
                        <PhotoCapture
                            show={showPhotoCapture}
                            onHide={() => setShowPhotoCapture(false)}
                            onCapture={(photo: FileData) => { setFileError(null); handleChange(photo); }}
                        />
                    )}

                    {/* Image modal for fullsize preview */}
                    {isImage && fileData && (
                        <ImageModal
                            show={showImageModal}
                            onHide={() => setShowImageModal(false)}
                            fileData={fileData}
                            filename={filename}
                        />
                    )}
                </>
            );

        case 'url':
            return (
                <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                    <Form.Label>
                        {field.displayName}
                        {field.required && <span className="text-danger">*</span>}
                    </Form.Label>
                    <div className="input-group">
                        <Form.Control
                            type="url"
                            value={value || ''}
                            onChange={(e) => handleChange(e.target.value)}
                            required={field.required}
                            disabled={isDisabled}
                            isInvalid={!!error}
                            placeholder="https://"
                            autoFocus={autoFocus}
                        />
                        {value && (
                            <a
                                href={value}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-secondary d-flex align-items-center"
                            >
                                <IconExternalLink size={18} />
                            </a>
                        )}
                    </div>
                    {error && <div className="invalid-feedback d-block">{error}</div>}
                </Form.Group>
            );

        case 'markdown':
            return (
                <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                    <Form.Label>
                        {field.displayName}
                        {field.required && <span className="text-danger">*</span>}
                    </Form.Label>
                    <div data-color-mode="light">
                        <MDEditor
                            value={value || ''}
                            onChange={(val) => handleChange(val || '')}
                            height={200}
                            preview={isDisabled ? 'preview' : 'live'}
                        />
                    </div>
                    {error && <div className="invalid-feedback d-block">{error}</div>}
                </Form.Group>
            );

        case 'barcode':
            return (
                <>
                    <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                        <Form.Label>
                            {field.displayName}
                            {field.required && <span className="text-danger">*</span>}
                        </Form.Label>
                        <div className="input-group">
                            <Form.Control
                                type="text"
                                value={value || ''}
                                onChange={(e) => handleChange(e.target.value)}
                                required={field.required}
                                disabled={isDisabled}
                                isInvalid={!!error}
                                placeholder="Naskenujte alebo zadajte kód"
                                autoFocus={autoFocus}
                            />
                            {!isDisabled && (
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => setShowBarcodeScanner(true)}
                                >
                                    <IconScan size={20} />
                                </Button>
                            )}
                        </div>
                        {error && <div className="invalid-feedback d-block">{error}</div>}
                    </Form.Group>
                    <BarcodeScanner
                        show={showBarcodeScanner}
                        onHide={() => setShowBarcodeScanner(false)}
                        onScan={(scannedValue) => handleChange(scannedValue)}
                    />
                </>
            );

        case 'password':
            const handlePasswordChange = (newPassword: string) => {
                setPasswordInput(newPassword);
                const mismatch = confirmPassword !== '' && newPassword !== confirmPassword;
                setPasswordMismatch(mismatch);
                // Only update form value when passwords match
                if (!mismatch && newPassword === confirmPassword) {
                    handleChange(newPassword);
                } else {
                    handleChange('');
                }
            };

            const handleConfirmChange = (confirm: string) => {
                setConfirmPassword(confirm);
                const mismatch = confirm !== '' && passwordInput !== confirm;
                setPasswordMismatch(mismatch);
                // Only update form value when passwords match
                if (!mismatch && passwordInput === confirm) {
                    handleChange(passwordInput);
                } else {
                    handleChange('');
                }
            };

            return (
                <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                    <Form.Label>
                        {field.displayName}
                        {field.required && <span className="text-danger">*</span>}
                    </Form.Label>
                    <div className="position-relative d-flex align-items-center mb-2">
                        <Form.Control
                            type={showPassword ? 'text' : 'password'}
                            value={passwordInput}
                            onChange={(e) => handlePasswordChange(e.target.value)}
                            required={field.required}
                            disabled={isDisabled}
                            isInvalid={!!error || passwordMismatch}
                            placeholder="Zadajte heslo"
                            className="password-field"
                            style={{paddingRight: '2.5rem'}}
                        />
                        <span
                            className="position-absolute text-muted d-flex align-items-center"
                            style={{right: '10px', cursor: isDisabled ? 'default' : 'pointer'}}
                            onClick={() => !isDisabled && setShowPassword(!showPassword)}
                        >
                            {showPassword ? <IconEyeOff size={14} /> : <IconEye size={14} />}
                        </span>
                    </div>
                    <div className="position-relative d-flex align-items-center">
                        <Form.Control
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => handleConfirmChange(e.target.value)}
                            required={field.required}
                            disabled={isDisabled}
                            isInvalid={passwordMismatch}
                            placeholder="Potvrďte heslo"
                            className="password-field"
                            style={{paddingRight: '2.5rem'}}
                        />
                        <span
                            className="position-absolute text-muted d-flex align-items-center"
                            style={{right: '10px', cursor: isDisabled ? 'default' : 'pointer'}}
                            onClick={() => !isDisabled && setShowConfirmPassword(!showConfirmPassword)}
                        >
                            {showConfirmPassword ? <IconEyeOff size={14} /> : <IconEye size={14} />}
                        </span>
                    </div>
                    {passwordMismatch && (
                        <small className="text-danger">Heslá sa nezhodujú</small>
                    )}
                    {error && !passwordMismatch && <div className="invalid-feedback d-block">{error}</div>}
                </Form.Group>
            );

        default:
            return (
                <Form.Group className={groupClassName} style={groupStyle} key={field.code} data-e2e={`field:${field.code}`}>
                    <Form.Label>
                        {field.displayName}
                        {field.required && <span className="text-danger">*</span>}
                    </Form.Label>
                    <Form.Control
                        type="text"
                        value={displayValue || (typeof value === 'object' ? '' : value)}
                        onChange={(e) => handleChange(e.target.value)}
                        required={field.required}
                        disabled={isDisabled}
                    />
                </Form.Group>
            );
    }
};
